import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useLayoutEffect, useMemo, useRef } from "react";
import FloorPlanCard from "./FloorPlanCard";
import { FloorPlanSliderSkeleton } from "./FloorPlanLibrarySkeleton";
import { CARD_MAX_WIDTH } from "../UploadStates/uploadCardLayout";

/**
 * Mobile-only horizontal floor-plan slider for `/main`.
 *
 * Layout contract (matches `UserMainHome`):
 * - The page (`UserMainHome` outer Box) has `px: { xs: 1.25, sm: 2 }` (10px / 16px).
 * - The upload card column has `maxWidth: CARD_MAX_WIDTH` (360px) and is centered
 *   below md, so on viewports wider than ~380px the upload card sits inset from
 *   the page padding.
 *
 * Edge-bleed behavior:
 * - The outer wrapper applies a one-level negative horizontal margin
 *   (`mx: { xs: -1.25, sm: -2 }`) that exactly cancels the page padding on its
 *   immediate parent in `UserMainHome`. The scroll container therefore reaches
 *   both viewport edges so the swipe gesture is captured across the entire
 *   screen and the next-card peek is visible right up to the edge.
 * - The internal `padding-left` / `padding-right` on the scroll container is
 *   then `max(pagePadding, calc((100% - CARD_MAX_WIDTH) / 2))`, which keeps
 *   the first card's left edge and the last card's right edge aligned with the
 *   upload card on every mobile/tablet viewport (small phones where the upload
 *   card spans full width minus page padding, large phones / tablets where the
 *   upload card is centered with `max-width: 360`).
 *
 * No `100vw` bleed hacks, no `left: 50%; margin-left: -50vw`. Single-level
 * cancellation of the immediate parent's padding only.
 */

/** xs page padding = `1.25 * 8` = 10px; sm page padding = `2 * 8` = 16px. */
const PAGE_PADDING_XS = "10px";
const PAGE_PADDING_SM = "16px";

const SCROLL_INSET_XS = `max(${PAGE_PADDING_XS}, calc((100% - ${CARD_MAX_WIDTH}px) / 2))`;
const SCROLL_INSET_SM = `max(${PAGE_PADDING_SM}, calc((100% - ${CARD_MAX_WIDTH}px) / 2))`;

const CARD_GAP_PX = 12;

/** Card sizing: ~78% of viewport, capped so two cards never exceed the upload-card lane on tablet. */
const CARD_WIDTH = `min(78vw, ${CARD_MAX_WIDTH - 80}px)`;

function MobileFloorPlanSlider({
  floorPlanItems,
  sectionTitle = "Floor plans",
  /** True while saved-layout tree is loading or refetching with no rows yet (avoids a false empty state). */
  loading = false,
  onCardClick,
  onCardDoubleClick,
  onMoreOptionsClick,
}) {
  const items = useMemo(
    () => (Array.isArray(floorPlanItems) ? floorPlanItems : []),
    [floorPlanItems]
  );
  const scrollRef = useRef(null);
  const prevFirstIdRef = useRef(undefined);

  /**
   * When a newer plan becomes the head of the list (typical after upload), the
   * DOM prepends a card but `scrollLeft` is unchanged — the viewport can stay
   * "on" older cards. Snap back to the start so the newest plan occupies the
   * leading slot (same as a fresh visit).
   */
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const firstId = items[0]?._id;
    if (!scroller || items.length === 0 || loading) {
      if (firstId !== undefined) prevFirstIdRef.current = firstId;
      return;
    }
    if (
      prevFirstIdRef.current !== undefined &&
      firstId !== undefined &&
      firstId !== prevFirstIdRef.current
    ) {
      scroller.scrollLeft = 0;
    }
    prevFirstIdRef.current = firstId;
  }, [items, loading]);

  return (
    <Box
      component="section"
      aria-label={sectionTitle}
      aria-busy={loading ? "true" : undefined}
      sx={{
        display: { xs: "block", md: "none" },
        // The viewport-edge bleed is provided by the parent Stack
        // (`UserMainHome` outer Stack) cancelling the page padding on mobile.
        // The slider just fills its parent's width.
        minWidth: 0,
        boxSizing: "border-box",
        pt: "10px",
        pb: "10px",
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontSize: "calc(0.8125rem + 4px)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: "text.primary",
          textAlign: "left",
          mb: 1,
          pl: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
          pr: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
          boxSizing: "border-box",
        }}
      >
        {sectionTitle}
      </Typography>

      {loading ? (
        <FloorPlanSliderSkeleton cardWidth={CARD_WIDTH} />
      ) : items.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            minHeight: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
            boxSizing: "border-box",
          }}
        >
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            No floor plans yet.
          </Typography>
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          role="list"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            gap: `${CARD_GAP_PX}px`,
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollSnapType: "x mandatory",
            scrollPaddingLeft: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
            scrollPaddingRight: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
            pl: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
            pr: { xs: SCROLL_INSET_XS, sm: SCROLL_INSET_SM },
            py: 0.5,
            boxSizing: "border-box",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            overscrollBehaviorX: "contain",
          }}
        >
          {items.map((item, idx) => (
            <Box
              key={item?._id ?? idx}
              role="listitem"
              sx={{
                flex: "0 0 auto",
                width: CARD_WIDTH,
                minWidth: 0,
                display: "flex",
                scrollSnapAlign: "start",
              }}
            >
              <FloorPlanCard
                item={item}
                onClick={() => onCardClick?.(item)}
                onDoubleClick={() => onCardDoubleClick?.(item)}
                onMoreOptionsClick={(it, e) => onMoreOptionsClick?.(it, e)}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

MobileFloorPlanSlider.propTypes = {
  floorPlanItems: PropTypes.array,
  sectionTitle: PropTypes.string,
  loading: PropTypes.bool,
  onCardClick: PropTypes.func,
  onCardDoubleClick: PropTypes.func,
  onMoreOptionsClick: PropTypes.func,
};

export default MobileFloorPlanSlider;
